import { FC, Fragment, ReactNode, useState } from 'react';

import { Menu } from '@headlessui/react';
import { ChevronDownIcon, DotsHorizontalIcon } from '@heroicons/react/outline';
import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { User } from '@shared/api/user/user.interface';
import { Link } from 'react-router-dom';

import { GiftItemInfoUsageLite } from '../utils/choiceUnitUsage.utils';
import {
  BrandDeliveryCategory,
  GiftBrandCategorySummary,
} from './GiftBrandCategorySummary';

const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';
const action =
  'shrink-0 whitespace-nowrap rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600';

const Detail: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="mb-1 text-xs font-medium text-gray-500">{label}</dt>
    <dd className="whitespace-pre-line break-words text-sm leading-6 text-gray-800">
      {children || '미설정'}
    </dd>
  </div>
);

const ProductSummary: FC<{
  label: string;
  items: GiftItemInfoUsageLite[] | null | undefined;
  onClick: () => void;
}> = ({ label, items, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full items-baseline gap-2 rounded py-1 text-left hover:text-indigo-700 focus-visible:outline-indigo-600"
  >
    <span className="shrink-0 text-xs text-gray-500">{label}</span>
    {items ? (
      items.length ? (
        <span className="text-sm tabular-nums">
          {items.length}개{' '}
          <span className="ml-1 text-xs text-gray-500">
            판매 중 {items.filter((item) => item.isEnabled !== false).length}
          </span>
        </span>
      ) : (
        <span className="text-sm text-gray-500 group-hover:text-indigo-700">
          미등록 · 관리
        </span>
      )
    ) : (
      <span className="text-xs text-gray-400">
        {items === null ? '조회 불가' : '불러오는 중'}
      </span>
    )}
    <span aria-hidden className="text-gray-400">
      ↗
    </span>
  </button>
);

export const GiftBrandRow: FC<{
  brand: GiftItemBrand;
  delivery: boolean;
  coupon: boolean;
  showSettlement: boolean;
  typeQuery: string;
  products: GiftItemInfoUsageLite[] | null | undefined;
  deliveryCategories: BrandDeliveryCategory[] | null | undefined;
  retryingCategories: boolean;
  onRetryCategories: () => void;
  onCategoryProducts: (categoryId?: string) => void;
  choices: GiftItemInfoUsageLite[] | null | undefined;
  sellers: User[] | null | undefined;
  deleting: boolean;
  onProducts: () => void;
  onChoices: () => void;
  onSellers: () => void;
  onDelete: () => void;
}> = ({
  brand,
  delivery,
  coupon,
  showSettlement,
  typeQuery,
  products,
  deliveryCategories,
  retryingCategories,
  onRetryCategories,
  onCategoryProducts,
  choices,
  sellers,
  deleting,
  onProducts,
  onChoices,
  onSellers,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const editUrl = `/gift-seller/item-brand/${brand._id}/edit${typeQuery}`;
  const d = brand.deliveryDetail;
  const si = brand.settlementInfo;
  const detailsId = `brand-details-${brand._id}`;
  return (
    <Fragment>
      <tr
        className={`block border-t border-gray-200 py-4 xl:table-row xl:py-0 ${
          expanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50/60'
        }`}
      >
        <th scope="row" className={`${cell} xl:w-1/4`}>
          <Link
            to={editUrl}
            className="break-words text-base font-semibold text-gray-900 hover:text-indigo-700 hover:underline"
          >
            {brand.name}
          </Link>
          <div className="mt-2 flex gap-1.5 text-xs font-normal text-gray-600">
            {delivery && (
              <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5">
                배송형
              </span>
            )}
            {coupon && (
              <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5">
                쿠폰형
              </span>
            )}
            {!delivery && !coupon && <span className="text-gray-500">유형 미지정</span>}
            {brand.thanksPrint?.enabled && (
              <span className="py-0.5 text-gray-500">감사인쇄 사용</span>
            )}
          </div>
        </th>
        <td className={cell}>
          <ProductSummary label="원상품" items={products} onClick={onProducts} />
          {(coupon || Boolean(choices?.length)) && (
            <ProductSummary label="선택상품" items={choices} onClick={onChoices} />
          )}
          <GiftBrandCategorySummary
            brandName={brand.name}
            products={products}
            categories={deliveryCategories}
            retrying={retryingCategories}
            onRetry={onRetryCategories}
            onManage={onCategoryProducts}
          />
        </td>
        {showSettlement && (
          <td className={cell}>
            <span className="mr-2 text-xs text-gray-500 xl:hidden">배송관리자</span>
            <button
              type="button"
              onClick={onSellers}
              className="rounded py-1 text-left text-sm text-gray-700 hover:text-indigo-700 hover:underline"
              title={sellers?.map((user) => user.info?.name || user.username).join(', ')}
            >
              {sellers ? (
                sellers.length ? (
                  <>
                    {sellers[0]?.info?.name || sellers[0]?.username}
                    {sellers.length > 1 && ` 외 ${sellers.length - 1}명`}
                  </>
                ) : (
                  '미등록 · 계정 관리'
                )
              ) : sellers === null ? (
                '조회 불가'
              ) : (
                '불러오는 중'
              )}
              <span aria-hidden className="ml-1 text-gray-400">
                ↗
              </span>
            </button>
          </td>
        )}
        <td className={`${cell} text-sm leading-6 text-gray-700`}>
          {delivery && (
            <>
              <p>
                {brand.allowIndividualDelivery === false
                  ? '일괄배송 전용'
                  : '개별배송 허용'}
              </p>
              <p className="text-xs text-gray-500">
                {d?.shippingFee != null
                  ? `추가금 ${d.shippingFee.toLocaleString()}원/개`
                  : '추가금 미설정'}
              </p>
            </>
          )}
          {coupon && (
            <>
              <p
                className="max-w-[220px] truncate"
                title={brand.couponDetail?.placeOfExchange}
              >
                {brand.couponDetail?.placeOfExchange || '교환처 미설정'}
              </p>
              <p
                className="max-w-[220px] truncate text-xs text-gray-500"
                title={brand.couponDetail?.validPeriod}
              >
                {brand.couponDetail?.validPeriod || '유효기간 미설정'}
              </p>
            </>
          )}
          {!delivery && !coupon && (
            <span className="text-gray-500">배송·쿠폰 정보 미설정</span>
          )}
        </td>
        <td className={cell}>
          <div className="flex items-center gap-1.5 xl:justify-end">
            <Link to={editUrl} className={action}>
              수정
            </Link>
            <button
              type="button"
              aria-label={`${brand.name} 상세 ${expanded ? '닫기' : '보기'}`}
              aria-expanded={expanded}
              aria-controls={detailsId}
              onClick={() => setExpanded(!expanded)}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-2 py-2 text-sm text-gray-600 hover:bg-gray-100"
            >
              상세{' '}
              <ChevronDownIcon
                aria-hidden
                className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`${brand.name} 더보기`}
                disabled={deleting}
                className="rounded p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
              >
                <DotsHorizontalIcon aria-hidden className="h-5 w-5" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-20 mt-1 w-36 rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      disabled={deleting}
                      onClick={onDelete}
                      className={`w-full rounded px-3 py-2 text-left text-sm text-red-700 ${
                        active ? 'bg-red-50' : ''
                      }`}
                    >
                      {deleting ? '삭제 중…' : '브랜드 삭제'}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr id={detailsId} className="block bg-gray-50 xl:table-row">
          <td
            colSpan={showSettlement ? 5 : 4}
            className="block px-5 pb-6 pt-2 xl:table-cell"
          >
            <dl className="grid gap-x-8 gap-y-5 border-t border-gray-200 pt-5 sm:grid-cols-2 xl:grid-cols-3">
              {delivery && (
                <>
                  <Detail label="배송 안내">{d?.shippingInfo}</Detail>
                  <Detail label="배송비 안내">{d?.shippingFeeInfo}</Detail>
                  <Detail label="소량주문 추가금">
                    {d?.shippingFee != null
                      ? `${d.shippingFee.toLocaleString()}원/개${
                          d.freeShippingMinQuantity != null
                            ? ` · ${d.freeShippingMinQuantity}개 이상 면제`
                            : ''
                        }`
                      : undefined}
                  </Detail>
                  <Detail label="배송 알림 수신번호">
                    {d?.notifyPhoneNumbers?.join(', ')}
                  </Detail>
                </>
              )}
              {coupon && (
                <>
                  <Detail label="쿠폰 교환처">
                    {brand.couponDetail?.placeOfExchange}
                  </Detail>
                  <Detail label="쿠폰 유효기간">{brand.couponDetail?.validPeriod}</Detail>
                </>
              )}
              {showSettlement && (
                <>
                  <Detail label="정산 주체 / 정산일">
                    {si?.accountEntity === AccountEntity.Mari
                      ? '마리'
                      : si?.accountEntity === AccountEntity.Gaon
                      ? '가온'
                      : '미정'}
                    {si?.settlementDay != null && ` · 익월 ${si.settlementDay}일`}
                  </Detail>
                  <Detail label="정산 추가단가">
                    {si?.shippingFee != null
                      ? `${si.shippingFee.toLocaleString()}원/개${
                          si.freeShippingMinQuantity != null
                            ? ` · ${si.freeShippingMinQuantity}개 이상 면제`
                            : ''
                        }`
                      : undefined}
                  </Detail>
                  <Detail label="정산 배송비">
                    {si?.deliveryFeeMode === 'orderDeliveryFee'
                      ? '고객 결제 배송비 전액 · 주문당 1회'
                      : si?.deliveryFeeMode === 'included'
                      ? '별도 지급 없음 · 상품 단가에 포함'
                      : si?.courierFee != null
                      ? `${si.courierFee.toLocaleString()}원/건${
                          si.courierFreeMinQuantity != null
                            ? ` · ${si.courierFreeMinQuantity}개 이상 무료`
                            : ''
                        }`
                      : undefined}
                  </Detail>
                </>
              )}
              <Detail label="등록일">
                {brand.createdAt
                  ? new Date(brand.createdAt).toLocaleDateString('ko-KR')
                  : undefined}
              </Detail>
            </dl>
          </td>
        </tr>
      )}
    </Fragment>
  );
};
